import type { MouseEvent, ReactElement } from 'react';
import { useEffect } from 'react';

import { useTracking } from '@skyscanner/strevda-tracking';
import { BpkCardV2, CARD_V2_VARIANTS } from '@skyscanner-internal/backpack-web/bpk-component-card';
import { BpkFlex, BpkVessel } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPressable, {
  PRESSABLE_RADIUS,
} from '@skyscanner-internal/backpack-web/bpk-component-pressable';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import classNames from 'classnames';
import { logOperationalEvent } from 'saddlebag-logger';

import type { PerformanceEventsPayloads, TrackingContext } from '@skyscanner/strevda-tracking';
import type { Pixels } from '@web-platform/shared-universal-types/src/network/endpoints/adsRetrievalService';
import type { URL } from '@web-platform/shared-universal-types-utils/src/utils';

import STYLES from './GlobalNavigationCard.module.scss';

type OwnProps = {
  position: string;
  title: string;
  link: URL;
  image: string;
  isSponsored: boolean;
  isSponsoredCardPresent: boolean;
  onClick: (e: MouseEvent<Element>) => Promise<void>;
  pixels: Pixels;
  analyticsProperties: TrackingContext;
  defaultTranslations: {
    headline: string;
    ctaUrl: string;
  };
  partnerName: string | null | undefined;
  performanceEventsPayloads?: PerformanceEventsPayloads;
};

export const GlobalNavigationCard = ({
  analyticsProperties,
  defaultTranslations,
  image,
  isSponsored,
  isSponsoredCardPresent,
  link,
  onClick,
  partnerName,
  performanceEventsPayloads,
  pixels,
  position,
  title,
}: OwnProps): ReactElement => {
  const { ref: trackingRef, trackClick } = useTracking<HTMLElement>(
    {
      analyticsProperties,
      pixels,
      performanceEventsPayloads,
    },
    {},
    false,
    getStrevdaTrackingConfig(),
  );

  const { ctaUrl, headline } = defaultTranslations;
  useEffect(() => {
    const payload = {
      CTAUrl: ctaUrl,
      headline,
      label: headline,
      navSlot: position,
    };
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_CARD_LOADED.NAME,
      strings: {
        ...payload,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_CARD_LOADED.NAME,
      component: 'GlobalNavigationCard',
      ...payload,
    });
  }, [headline, ctaUrl, position]);

  return (
    // TODO: CLOV-1913 — BpkVessel retained: this wrapper uses CSS :first-child/:last-child to apply
    // edge spacing based on DOM position. BpkBox/BpkFlex cannot take className/style;
    // replacing it cleanly would require passing positional props from the parent.
    <BpkVessel
      className={classNames(STYLES.container, {
        [STYLES.sponsoredCard]: isSponsoredCardPresent,
      })}
    >
      <BpkPressable
        ref={trackingRef}
        as="a"
        radius={PRESSABLE_RADIUS.md}
        fill
        href={link}
        onClick={(e: MouseEvent<Element>) => {
          trackClick();
          return onClick(e);
        }}
        aria-label={
          isSponsored
            ? `${title}. ${translate('ktxt_promotion_sponsored_by_partner', {
                partner: partnerName,
              })}.`
            : title
        }
      >
        <BpkCardV2.Root variant={CARD_V2_VARIANTS.homepageNav}>
          {/* TODO: CLOV-1913 — BpkVessel retained: .cardContent needs className for .sponsoredCard nested override
              and uses bpk-breakpoint-above-desktop (1281px) which has no BpkFlex responsive key. */}
          <BpkVessel className={STYLES.cardContent}>
            <BpkFlex direction="column" align="flex-start">
              <img
                alt=""
                src={image}
                className={getLogoClassName(isSponsoredCardPresent, isSponsored)}
              />
              {isSponsored && (
                <BpkText
                  id={`${position}-sponsored`}
                  className={STYLES.sponsoredText}
                  aria-hidden="true"
                >
                  {translate('ktxt_promotion_sponsored')}
                </BpkText>
              )}
            </BpkFlex>
            <BpkText
              className={STYLES.truncate}
              id={position}
              textStyle={TEXT_STYLES.heading5}
              color={TEXT_COLORS.textOnDark}
            >
              {title}
            </BpkText>
          </BpkVessel>
        </BpkCardV2.Root>
      </BpkPressable>
    </BpkVessel>
  );
};

const getLogoClassName = (isSponsoredCardPresent: boolean, isSponsored: boolean) => {
  if (isSponsoredCardPresent) {
    return isSponsored ? STYLES.sponsoredLogo : STYLES.logoImageSponsoredPresent;
  }
  return STYLES.logoImage;
};
